import React, { useCallback, useEffect, useRef } from 'react';
import './styles.scss';
import { FieldComponent } from '../Field';
import {
  useConfigStore,
  useChatStore,
  useWidgetStore,
  useContextStore,
} from '@/hooks/stores';
import { useDynamicFieldsForm } from '@/hooks';
import { shouldRenderFieldByDependsOn } from '@/utils';
import { createFieldContextHelpers } from '@/utils/field-context-helpers';
import { resolveStringOrPageContextFunction } from '@/utils/page-context';
import { Config } from '@/types';
import { parsePhoneNumber } from '@/utils/phone-utils';
import Components from './components';

/**
 * Detect browser-autofilled values that bypassed React's onChange.
 *
 * Browser autofill and extensions can set DOM input values without
 * firing synthetic events, leaving Formik state stale. Returns
 * overrides for fields where the DOM value differs from Formik.
 */
function collectAutofillValues(
  fields: Config.Field[],
  formikValues: Record<string, string>
): Record<string, string> {
  const overrides: Record<string, string> = {};

  for (const field of fields) {
    const el = document.getElementById(`peptalk-${field.name}`);
    if (
      !(el instanceof HTMLInputElement) &&
      !(el instanceof HTMLTextAreaElement) &&
      !(el instanceof HTMLSelectElement)
    ) {
      continue;
    }

    const domValue = el.value.trim();
    const formikValue = (formikValues[field.name] ?? '').trim();

    // Skip if DOM matches Formik or DOM is empty
    if (!domValue || domValue === formikValue) continue;

    if (field.type === 'tel') {
      // Phone autofill typically provides a formatted international number.
      // Strip formatting, attempt E.164 conversion for validation compatibility.
      const cleaned = domValue.replace(/[\s\-().]/g, '');

      // PhoneInput blur already set the correct E.164 in formik — DOM only
      // shows the national part, so skip to avoid corrupting the value.
      if (
        formikValue.startsWith('+') &&
        formikValue.replace(/\D/g, '').endsWith(cleaned)
      ) {
        continue;
      }

      const withPlus = cleaned.startsWith('+') ? cleaned : `+${cleaned}`;
      const parsed = parsePhoneNumber(withPlus);
      // Use E.164 if parseable, otherwise raw cleaned value (validation will catch invalid numbers)
      overrides[field.name] = parsed ? parsed.number : cleaned;
    } else {
      overrides[field.name] = domValue;
    }
  }

  return overrides;
}

interface Props {
  disabled?: boolean;
  onSubmitQuestionnaire: (values: Record<string, string>) => void;
  onFirstInteraction?: () => void;
}

export const Questionnaire: React.FC<Props> = ({
  onSubmitQuestionnaire,
  onFirstInteraction,
  disabled = false,
}) => {
  const config = useConfigStore();
  const colors = config.theme.colors;
  const fields = config.features.chat.fields;
  const { filledQuestionnaireData, isFormSubmitting } = useChatStore();
  const { pageContext, customContext } = useContextStore();
  const userCountryCode = useWidgetStore(
    state => state.location.userCountryCode
  );

  const hasInteractedRef = useRef(false);

  const { formik, onChange, onBlur } = useDynamicFieldsForm({
    fields,
    initialValues: filledQuestionnaireData || {},
    userCountryCode,
    onChange: () => {
      if (!hasInteractedRef.current && onFirstInteraction) {
        hasInteractedRef.current = true;
        onFirstInteraction();
      }
    },
    onSubmit: values => {
      // This onSubmit is not used directly - we call onSubmitQuestionnaire in handleSubmit instead
      // to ensure hidden fields with function defaultValue are included
      onSubmitQuestionnaire(values);
    },
  });

  // Store formik.setFieldValue in ref to avoid dependency issues
  const setFieldValueRef = useRef(formik.setFieldValue);
  useEffect(() => {
    setFieldValueRef.current = formik.setFieldValue;
  }, [formik.setFieldValue]);

  // Resolve defaultValue for hidden fields and function for function fields when component mounts or context changes
  useEffect(() => {
    for (const field of fields) {
      if (field.type === 'hidden' || field.type === 'function') {
        const currentValue = formik.values[field.name];
        const hasValue =
          currentValue !== undefined &&
          currentValue !== null &&
          currentValue !== '';

        // Initialize hidden/function field in formik if it doesn't exist
        if (!(field.name in formik.values)) {
          // For function type fields, use field.function; for hidden fields, use field.defaultValue
          const valueToResolve =
            field.type === 'function' ? field.function : field.defaultValue;

          if (valueToResolve) {
            const resolvedValue = resolveStringOrPageContextFunction({
              value: valueToResolve,
              pageContext: pageContext || null,
              customContext: customContext || null,
              fieldName: field.name,
            });

            if (resolvedValue !== undefined && resolvedValue !== null) {
              setFieldValueRef.current(field.name, resolvedValue);
            } else {
              setFieldValueRef.current(field.name, '');
            }
          } else {
            setFieldValueRef.current(field.name, '');
          }
        } else {
          // Resolve value if provided and field doesn't have a value
          const valueToResolve =
            field.type === 'function' ? field.function : field.defaultValue;

          if (valueToResolve && !hasValue) {
            const resolvedValue = resolveStringOrPageContextFunction({
              value: valueToResolve,
              pageContext: pageContext || null,
              customContext: customContext || null,
              fieldName: field.name,
            });

            if (resolvedValue !== undefined && resolvedValue !== null) {
              setFieldValueRef.current(field.name, resolvedValue);
            }
          }
        }
      }
    }
  }, [fields, pageContext, customContext]);

  const scrollToFirstError = useCallback(
    (errors: Record<string, string | undefined>) => {
      const visibleFields = fields.filter(field =>
        shouldRenderFieldByDependsOn({ field, formik })
      );

      const firstErrorField = visibleFields.find(field => errors[field.name]);

      if (firstErrorField) {
        const fieldId = `peptalk-${firstErrorField.name}`;
        const inputElement = document.getElementById(fieldId);

        if (inputElement) {
          const formGroupElement =
            inputElement.closest('.peptalk-form-group') || inputElement;

          formGroupElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });

          if (inputElement instanceof HTMLElement) {
            setTimeout(() => {
              inputElement.focus();
            }, 300);
          }
        }
      }
    },
    [fields, formik]
  );

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();

      // Resolve hidden and function field values
      // Hidden fields use defaultValue, function fields use function field
      // Note: Function strings are already converted to functions in config.ts
      // All hidden/function fields should be included in submission, even if function fails
      const resolvedHiddenValues: Record<string, string> = {};

      // Process all hidden and function fields
      const allHiddenAndFunctionFields = fields.filter(
        f => f.type === 'hidden' || f.type === 'function'
      );

      for (const field of allHiddenAndFunctionFields) {
        // For function type fields, use field.function; for hidden fields, use field.defaultValue
        const valueToResolve =
          field.type === 'function' ? field.function : field.defaultValue;

        if (!valueToResolve) {
          console.error(
            `[Questionnaire] ${field.type} field "${field.name}" missing ${field.type === 'function' ? 'function' : 'defaultValue'} - this should not happen (config validation should have caught this)`
          );
          resolvedHiddenValues[field.name] = '';
          continue;
        }

        const resolvedValue = resolveStringOrPageContextFunction({
          value: valueToResolve,
          pageContext: pageContext || null,
          customContext: customContext || null,
          fieldName: field.name,
        });

        // Always include hidden/function fields in submission, even if empty (function failed)
        // This ensures all configured fields are present in formData
        if (
          resolvedValue !== undefined &&
          resolvedValue !== null &&
          resolvedValue !== ''
        ) {
          resolvedHiddenValues[field.name] = String(resolvedValue);
        } else {
          // Function failed or returned empty - include as empty string
          // This ensures the field is present in formData for tracking/debugging
          resolvedHiddenValues[field.name] = '';

          if (typeof valueToResolve === 'function') {
            // Log when a function fails to resolve (returns empty/undefined)
            // This helps debug issues like undefined analytics, etc.
            console.warn(
              `[Questionnaire] ${field.type} field "${field.name}" function returned empty value - check console for function execution errors`
            );
          }
        }
      }

      // Update formik with resolved hidden values (including empty ones)
      for (const fieldName of Object.keys(resolvedHiddenValues)) {
        formik.setFieldValue(fieldName, resolvedHiddenValues[fieldName]);
      }

      // Validate visible fields
      const visibleFields = fields.filter(field =>
        shouldRenderFieldByDependsOn({ field, formik })
      );
      for (const field of visibleFields) {
        formik.setFieldTouched(field.name, true);
      }

      // Sync browser-autofilled values that bypassed React onChange
      const autofillOverrides = collectAutofillValues(
        visibleFields,
        formik.values
      );
      for (const [fieldName, value] of Object.entries(autofillOverrides)) {
        formik.setFieldValue(fieldName, value);
      }

      await new Promise(resolve => setTimeout(resolve, 0));
      const errors = await formik.validateForm();

      if (Object.keys(errors).length > 0) {
        setTimeout(() => {
          scrollToFirstError(errors);
        }, 100);
        return;
      }

      // Submit with resolved hidden values overriding formik.values
      onSubmitQuestionnaire({
        ...formik.values,
        ...resolvedHiddenValues,
      });
    },
    [
      fields,
      formik,
      onSubmitQuestionnaire,
      scrollToFirstError,
      pageContext,
      customContext,
    ]
  );

  // Helper function to resolve field options (static or dynamic)
  const resolveFieldOptions = (field: Config.Field) => {
    if (field.dynamicOptions) {
      try {
        const fieldContextHelpers = createFieldContextHelpers(userCountryCode);
        const result = field.dynamicOptions(formik.values, fieldContextHelpers);
        if (Array.isArray(result) && result.length > 0) {
          return result;
        }
        return field.options || [];
      } catch (error) {
        console.warn(
          `Error evaluating dynamicOptions for field ${field.name}:`,
          error
        );
        return field.options || [];
      }
    }
    return field.options || [];
  };

  return (
    <Components.Container isFormSubmitting={isFormSubmitting}>
      {fields
        .filter(field => {
          // Filter out hidden fields from visible rendering
          if (field.type === 'hidden') return false;
          return shouldRenderFieldByDependsOn({ field, formik });
        })
        .map((field, index) => {
          // Resolve options for this field
          const resolvedOptions = resolveFieldOptions(field);

          return (
            <FieldComponent
              key={field.name}
              index={index}
              {...field}
              options={resolvedOptions}
              value={formik.values[field.name]}
              error={
                formik.touched[field.name]
                  ? formik.errors[field.name]
                  : undefined
              }
              disabled={disabled || isFormSubmitting}
              formValues={formik.values}
              onChange={onChange}
              onBlur={onBlur}
            />
          );
        })}
      {!disabled && (
        <Components.SubmitButton
          type="submit"
          disabled={isFormSubmitting}
          className="gap-xs"
          onClick={handleSubmit}
        >
          {isFormSubmitting ? (
            <Components.SubmitLoading />
          ) : (
            <Components.SubmitIcon color={colors.primary} />
          )}
          <Components.SubmitText isFormSubmitting={isFormSubmitting} />
        </Components.SubmitButton>
      )}
    </Components.Container>
  );
};
